/* eslint-disable no-plusplus */
import { useEffect, useMemo, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { nanoid } from 'nanoid';

dayjs.extend(localeData);

function getWeeks(
  date: Dayjs,
  selected: Dayjs,
  minDate: Dayjs,
  maxDate: Dayjs,
) {
  const prevDays = [];
  const nextDays = [];
  let currentMonthDays = [];

  for (let i = date.startOf('month').get('day') % 7; i > 0; i--) {
    const day: Dayjs = date.startOf('month').subtract(i, 'day');
    const isDisabled = day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day');
    prevDays.push({
      date: day,
      isToday: day.isSame(dayjs(), 'day'),
      isSelected: day.isSame(selected, 'day'),
      isDisabled,
      isNextMonth: false,
      isPrevMonth: true,
    });
  }
  for (let i = 1; i < 7 - (date.endOf('month').get('day') % 7); i++) {
    const day: Dayjs = date.endOf('month').add(i, 'day');
    const isDisabled = day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day');

    nextDays.push({
      date: day,
      isToday: day.isSame(dayjs(), 'day'),
      isSelected: day.isSame(selected, 'day'),
      isDisabled,
      isNextMonth: true,
      isPrevMonth: false,
    });
  }

  currentMonthDays = Array.from({ length: date.daysInMonth() }, (_, i) => {
    const day: Dayjs = date.set('date', i + 1);
    const isDisabled = day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day');

    return {
      date: day,
      isToday: day.isSame(dayjs(), 'day'),
      isSelected: day.isSame(selected, 'day'),
      isDisabled,
      isNextMonth: false,
      isPrevMonth: false,
    };
  });

  const days = [...prevDays, ...currentMonthDays, ...nextDays];

  return dayjs.weekdaysShort().map((weekday, index) => ({
    id: nanoid(),
    weekday,
    days: days.slice(index * 7, index * 7 + 7),
  }));
}

type DayType = {
  date: Dayjs;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isNextMonth: boolean;
  isPrevMonth: boolean;
};

type WeekType = {
  id: string;
  weekday: string;
  days: DayType[];
};

type UseCalendarType = {
  currentOffset?: number;
  selected?: Dayjs;
  date?: Dayjs;
  onSelect?: (_: DayType) => void;
  onChangePage?: (_: number) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
};
type UseCalendarReturnType = {
  id: string;
  date: Dayjs;
  selected: Dayjs;
  onNextPage: () => void;
  onPrevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onSelect: (_: DayType) => void;
  onChangePage: (_: number) => void;
  weeks: WeekType[];
};

export type {
  UseCalendarType, UseCalendarReturnType, WeekType, DayType,
};

export function useCalendar({
  currentOffset = 0,
  selected = dayjs(),
  date = dayjs(),
  onSelect = () => null,
  onChangePage = () => null,
  minDate = null,
  maxDate = null,
}: UseCalendarType): UseCalendarReturnType {
  const currentDate = useMemo(() => selected ?? date, [selected, date]);
  const [offset, setOffset] = useState(currentOffset);
  const id = useMemo(nanoid, []);

  useEffect(() => {
    setOffset(currentOffset);
  }, [currentOffset]);

  useEffect(() => {
    onChangePage(offset);
  }, [offset]);

  return {
    id,
    date: currentDate.add(offset, 'month'),
    selected,
    onNextPage: () => setOffset((prev) => prev + 1),
    onPrevPage: () => setOffset((prev) => prev - 1),
    hasNextPage: maxDate ? currentDate.isBefore(maxDate) : true,
    hasPrevPage: minDate ? currentDate.isAfter(minDate) : true,
    onSelect: (day) => {
      onSelect(day);
      setOffset(0);
    },
    onChangePage,
    weeks: getWeeks(
      currentDate.add(offset, 'month'),
      selected,
      minDate,
      maxDate,
    ),
  };
}
